import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { useRentalCategories } from '@/hooks/useRentalCategories';
import { RentalVehicleFilters } from '@/hooks/useRentalVehicles';

interface VehicleFiltersProps {
  onFiltersChange: (filters: RentalVehicleFilters) => void;
}

export const VehicleFilters = ({ onFiltersChange }: VehicleFiltersProps) => {
  const { data: categories } = useRentalCategories();
  const [filters, setFilters] = useState<RentalVehicleFilters>({});
  const [priceRange, setPriceRange] = useState([0, 500]);

  const handleCategoryChange = (value: string) => {
    const newFilters = { ...filters, category: value === 'all' ? undefined : value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleTransmissionChange = (value: string) => {
    const newFilters = { ...filters, transmission: value === 'all' ? undefined : value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handlePassengersChange = (value: string) => {
    const newFilters = { ...filters, passengers: value === 'all' ? undefined : parseInt(value) };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handlePriceChange = (value: number[]) => {
    setPriceRange(value);
  };

  const applyPriceFilter = () => {
    const newFilters = { ...filters, minPrice: priceRange[0], maxPrice: priceRange[1] };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setPriceRange([0, 500]);
    onFiltersChange({});
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Filters</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <Label className="mb-2 block">Category</Label>
          <Select onValueChange={handleCategoryChange} defaultValue="all">
            <SelectTrigger>
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories?.map((category) => (
                <SelectItem key={category.id} value={category.id}>
                  {category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-2 block">Price Range (per day)</Label>
          <div className="px-2 pt-2">
            <Slider
              value={priceRange}
              onValueChange={handlePriceChange}
              max={500}
              step={10}
              className="mb-4"
            />
            <div className="flex justify-between text-sm text-muted-foreground mb-2">
              <span>${priceRange[0]}</span>
              <span>${priceRange[1]}</span>
            </div>
            <Button onClick={applyPriceFilter} className="w-full" size="sm">
              Apply Price Filter
            </Button>
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Transmission</Label>
          <Select onValueChange={handleTransmissionChange} defaultValue="all">
            <SelectTrigger>
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="automatic">Automatic</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="mb-2 block">Passengers</Label>
          <Select onValueChange={handlePassengersChange} defaultValue="all">
            <SelectTrigger>
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any</SelectItem>
              <SelectItem value="2">2+</SelectItem>
              <SelectItem value="4">4+</SelectItem>
              <SelectItem value="5">5+</SelectItem>
              <SelectItem value="7">7+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button onClick={clearFilters} variant="outline" className="w-full">
          Clear All Filters
        </Button>
      </CardContent>
    </Card>
  );
};